import { createContext, useCallback, useContext, useEffect, useState, type CSSProperties, type MouseEvent, type ReactNode } from 'react';
import type { Cohort, RmCohort } from '../shared/q3';

/**
 * Tiny History-API router: each page has a real path, so browser back /
 * forward, refresh and shared links all work. The server already serves
 * index.html for any non-API path.
 */

export type View = 'home' | 'me' | Cohort | 'list';

export interface Route {
  view: View;
  /**
   * ?rm=EMP.ID — on My Progress, someone looked up instead of the viewer; on a
   * board, the row to scroll to and highlight.
   */
  lookId: string | null;
  /** List page only: which summary category (e.g. "past-halfway"), optionally within one cohort. */
  list?: { cat: string; cohort: RmCohort | null };
}

const PATHS: Record<View, string> = {
  home: '/',
  me: '/my-progress',
  'Rising Star': '/rising-stars',
  'Super Star': '/super-stars',
  'Star Force': '/star-force',
  list: '/list',
};
const VIEWS = Object.keys(PATHS) as View[];

const COHORT_SLUG: Record<RmCohort, string> = { 'Rising Star': 'rising-stars', 'Super Star': 'super-stars' };

export const pathFor = (r: Route): string => {
  if (r.view === 'list') {
    const cohort = r.list?.cohort ? '?cohort=' + COHORT_SLUG[r.list.cohort] : '';
    return '/list/' + encodeURIComponent(r.list?.cat ?? '') + cohort;
  }
  return PATHS[r.view] + (r.view !== 'home' && r.lookId ? '?rm=' + encodeURIComponent(r.lookId) : '');
};

/** Earlier builds used #my-progress style links; keep those working. */
function legacyHashView(): View | null {
  const hash = window.location.hash.slice(1);
  if (!hash) return null;
  return VIEWS.find((v) => PATHS[v].slice(1) === hash) ?? (hash === 'home' ? 'home' : null);
}

export function readRoute(): Route {
  const legacy = legacyHashView();
  const path = window.location.pathname.replace(/\/+$/, '') || '/';
  if (!legacy && path.startsWith('/list/')) {
    const slug = new URLSearchParams(window.location.search).get('cohort');
    const cohort = (Object.keys(COHORT_SLUG) as RmCohort[]).find((c) => COHORT_SLUG[c] === slug) ?? null;
    return { view: 'list', lookId: null, list: { cat: decodeURIComponent(path.slice('/list/'.length)), cohort } };
  }
  const view = legacy ?? VIEWS.find((v) => PATHS[v] === path) ?? 'home';
  const lookId = view !== 'home' ? new URLSearchParams(window.location.search).get('rm') : null;
  return { view, lookId };
}

interface HistoryState {
  /** Scroll position to restore when the user comes back to this entry. */
  y?: number;
}

/** Where to scroll once the new page has rendered. */
export type ScrollTarget = { rowId: string } | { y: number };

type Navigate = (to: Route) => void;

/** A board link with ?rm= scrolls to that row; everything else starts at the top. */
const scrollFor = (r: Route): ScrollTarget => (r.view !== 'me' && r.lookId ? { rowId: r.lookId } : { y: 0 });

const RouterContext = createContext<Navigate>(() => {});

/** Navigate from inside a component (for clickable rows that aren't links). */
export const useNavigate = (): Navigate => useContext(RouterContext);

export function useRouter(): { route: Route; navigate: Navigate; scroll: ScrollTarget | null; clearScroll: () => void } {
  const [route, setRoute] = useState<Route>(readRoute);
  const [scroll, setScroll] = useState<ScrollTarget | null>(() => scrollFor(readRoute()));

  useEffect(() => {
    window.history.scrollRestoration = 'manual';
    // Rewrite an old #hash link to its real path without adding a history entry.
    if (legacyHashView()) window.history.replaceState({ y: 0 }, '', pathFor(readRoute()));
    const onPop = (e: PopStateEvent) => {
      setRoute(readRoute());
      setScroll({ y: (e.state as HistoryState | null)?.y ?? 0 });
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const navigate = useCallback<Navigate>((to) => {
    const path = pathFor(to);
    if (path === window.location.pathname + window.location.search) {
      setScroll(scrollFor(to));
      return;
    }
    // Remember where we were on the page we're leaving, for the back button.
    window.history.replaceState({ ...(window.history.state ?? {}), y: window.scrollY }, '');
    window.history.pushState({ y: 0 } satisfies HistoryState, '', path);
    setRoute(to);
    setScroll(scrollFor(to));
  }, []);

  return { route, navigate, scroll, clearScroll: useCallback(() => setScroll(null), []) };
}

export function RouterProvider({ navigate, children }: { navigate: Navigate; children: ReactNode }) {
  return <RouterContext.Provider value={navigate}>{children}</RouterContext.Provider>;
}

/**
 * A real <a href>, so links can be opened in a new tab or copied, but a plain
 * click navigates in-app without a page reload.
 */
export function Link({
  to,
  className,
  children,
  ...rest
}: {
  to: Route;
  className?: string;
  children: ReactNode;
  'aria-label'?: string;
  'aria-selected'?: boolean;
  role?: string;
  title?: string;
  style?: CSSProperties;
}) {
  const navigate = useContext(RouterContext);
  const onClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    navigate(to);
  };
  return (
    <a href={pathFor(to)} className={className} onClick={onClick} {...rest}>
      {children}
    </a>
  );
}

export const to = (view: View, lookId: string | null = null): Route => ({ view, lookId });

export const toList = (cat: string, cohort: RmCohort | null = null): Route => ({ view: 'list', lookId: null, list: { cat, cohort } });
